import { useMemo, useContext } from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import Delete from '@mui/icons-material/Delete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion } from '@mui/material'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import IconButton from '@mui/material/IconButton'
import { string, object, bool, func } from 'prop-types'
import { integrationContext, modalContext } from '@contexts'
import {
  S3out,
  Azure,
  SFTP,
  SFTPInbound,
  Box,
  GoogleCloud,
  FacebookAdsSync,
  GoogleDCM,
  Bitly,
  GoogleDCMOutput,
  Email,
  FacebookAdsLink,
  S3in,
  QrCodeMonkey,
  AdobeInbound,
  Workfront,
  AdobeExperienceManagerInbound,
  AdobeExperienceManagerOutbound,
  Branch,
  GoogleAnalytics,
  GoogleAdsInbound,
  AmazonAds,
  SnapchatMarketingInbound,
  SnapchatMarketingOutbound,
  Pinterest,
  PinterestOutbound,
  FacebookMarketing,
  FacebookMarketingOutbound,
  GoogleCM360Inbound,
  GoogleCM360Outbound,
  GoogleDV360Inbound,
  GoogleDV360Outbound,
  TikTokInbound,
  TikTokOutbound,
  SnowflakeOutbound,
  BoxPushV2,
  SalesforceMarketingCloudInbound,
  SalesforceMarketingCloudOutbound,
} from './integration-forms'
import { templateCopyContext } from './context'

const HeightLimitedImg = styled.img`
  max-height: 24px;
  margin-right: 12px;
`

const SpaceBetween = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const displayBlock = css`
  display: inline-flex;
  align-items: center;
`

const integrationForms = {
  'box-push': Box, // or box-push-general ?
  'box-push-general': Box, // or box-push-general ?
  'box-push-v2': BoxPushV2, // new box integration (merging the two old ones into one)
  's3-push': S3out,
  'azure-blob-push': Azure,
  'sftp-push': SFTP,
  'sftp-pull': SFTPInbound,
  'google-cloud': GoogleCloud,
  'facebook-ads': FacebookAdsSync,
  'google-dcm': GoogleDCM,
  bitly: Bitly,
  'google-dcm-output': GoogleDCMOutput,
  'email-output': Email,
  'workfront-project-pull-task-trigger': Workfront,
  'facebook-ads-update': FacebookAdsLink,
  'aws-s3-inbound': S3in,
  'qr-code-monkey': QrCodeMonkey,
  'adobe-inbound': AdobeInbound,
  'adobe-experience-manager-inbound': AdobeExperienceManagerInbound,
  'adobe-experience-manager-outbound': AdobeExperienceManagerOutbound,
  branch: Branch,
  'google-analytics': GoogleAnalytics,
  'google-ads-inbound': GoogleAdsInbound,
  'amazon-ads-inbound': AmazonAds,
  'snapchat-marketing-inbound': SnapchatMarketingInbound,
  'snapchat-marketing-outbound': SnapchatMarketingOutbound,
  'pinterest-inbound': Pinterest,
  'pinterest-outbound': PinterestOutbound,
  'facebook-marketing-inbound': FacebookMarketing,
  'facebook-marketing-outbound': FacebookMarketingOutbound,
  'google-cm360-inbound': GoogleCM360Inbound,
  'google-cm360-outbound': GoogleCM360Outbound,
  'tiktok-inbound': TikTokInbound,
  'snowflake-push': SnowflakeOutbound,
  'google-dv360-inbound': GoogleDV360Inbound,
  'google-dv360-outbound': GoogleDV360Outbound,
  'tiktok-outbound': TikTokOutbound,
  'salesforce-marketing-cloud-inbound': SalesforceMarketingCloudInbound,
  'salesforce-marketing-cloud-outbound': SalesforceMarketingCloudOutbound,
}

export const IntegrationExpansionPanel = ({
  assignment,
  open = false,
  type = 'output',
  onSelect = () => {},
  onDelete = () => {},
  onMultiDelete = () => {},
}) => {
  const { template } = useContext(templateCopyContext)
  const ModalContext = useContext(modalContext)
  const { inboundById, outboundById } = useContext(integrationContext)

  const integration = useMemo(
    () =>
      type === 'input'
        ? inboundById[assignment.integration_id]
        : outboundById[assignment.integration_id],
    [type, inboundById, outboundById, assignment],
  )
  const Form = useMemo(() => integrationForms[integration.slug] || Email, [integration.slug])

  const integrationsInboundDependant = []

  function checkIfIntegrationHasSyncedOutbound() {
    const dependentIntegrations = integrationsInboundDependant.find(
      i =>
        i.inbound === integration.slug &&
        template.integrations.find(int => int.slug === i.outbound),
    )
    if (dependentIntegrations) {
      return dependentIntegrations
    }
    return false
  }

  function handleIntegrationDeletion(event) {
    event.stopPropagation()
    const dependentIntegrations = checkIfIntegrationHasSyncedOutbound()
    if (dependentIntegrations) {
      confirmDeleteModalWithDependents(dependentIntegrations)
    } else {
      confirmDeleteModal()
    }
  }

  const confirmDeleteModalWithDependents = dependentIntegrations => {
    const dependentSlug =
      type === 'input' ? dependentIntegrations.outbound : dependentIntegrations.inbound

    const dependent = template.integrations.find(i => i.slug === dependentSlug)

    ModalContext.displayModal({
      title: 'Are you sure?',
      description: `Deleting this integration will also delete the ${
        type === 'input' ? 'outbound' : 'inbound'
      } integration of the same type on this template.`,
      confirm: () => {
        return () => {
          if (dependent) {
            onMultiDelete([assignment.id, dependent.id])
          } else {
            onDelete(assignment.id)
          }
        }
      },
    })
  }

  const confirmDeleteModal = () => {
    ModalContext.displayModal({
      title: 'Are you sure?',
      description: `You are about to delete your connection to ${assignment.name}, and will lose all mappings, filters and configuration for this integration. Are you sure you want to continue?`,
      confirm: () => {
        return () => {
          onDelete(assignment.id)
        }
      },
    })
  }

  return (
    <Accordion expanded={open} onChange={onSelect}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1bh-content"
        id="panel1bh-header"
      >
        <SpaceBetween>
          <div css={displayBlock}>
            <HeightLimitedImg
              src={`https://claravine-public-prod.s3-us-west-2.amazonaws.com/images/${integration.slug}.png`}
              alt={template.integrationsById[assignment.id].name}
            />
            <div>{`${integration.name} - ${assignment.name ? assignment.name : ''}`}</div>
          </div>
          <IconButton
            onClick={handleIntegrationDeletion}
            aria-label="delete-integration"
            component="span"
            size="small"
          >
            <Delete />
          </IconButton>
        </SpaceBetween>
      </AccordionSummary>
      <AccordionDetails>
        <Form integration={integration} assignment={assignment} template={template} />
      </AccordionDetails>
    </Accordion>
  )
}

IntegrationExpansionPanel.propTypes = {
  type: string,
  assignment: object.isRequired,
  open: bool,
  onSelect: func,
  onDelete: func,
  onMultiDelete: func,
}
